import React, { useMemo } from 'react';
import type { BadgeDesign, IssuedBadge } from '../../types/discoveryBadges';
import './BadgeStack.css';

const ICON_LABELS: Record<string, string> = {
  trophy: 'Trophy',
  laurel: 'Laurel',
  checkered_flag: 'Checkered flag',
  steering_wheel: 'Steering wheel',
  helmet: 'Helmet',
  wheel: 'Wheel',
  engine: 'Engine',
  turbo: 'Turbo',
  wrench: 'Wrench',
  track: 'Track',
  shield: 'Shield',
  crown: 'Crown',
  star: 'Star',
  club_crest: 'Club crest',
  location_pin: 'Pin',
  number_plate: 'Plate',
};

/** Stable SVG silhouettes — viewBox 0 0 64 64, centered content area. */
function shapePath(shape: string): string {
  switch (shape) {
    case 'shield':
      return 'M32 4 L56 12 V34 C56 48 44 56 32 60 C20 56 8 48 8 34 V12 Z';
    case 'hexagon':
      return 'M20 6 H44 L58 32 L44 58 H20 L6 32 Z';
    case 'pennant':
      return 'M8 8 H56 L48 56 H8 Z';
    case 'crest':
      return 'M32 4 L54 12 V30 C54 46 42 54 32 60 C22 54 10 46 10 30 V12 Z';
    case 'plaque':
      return 'M10 8 H54 L60 16 V48 L54 56 H10 L4 48 V16 Z';
    case 'rounded_rectangle':
      return 'M12 8 H52 A4 4 0 0 1 56 12 V52 A4 4 0 0 1 52 56 H12 A4 4 0 0 1 8 52 V12 A4 4 0 0 1 12 8 Z';
    case 'circle':
    case 'roundel':
    default:
      return 'M32 4 A28 28 0 1 1 31.9 4 Z';
  }
}

export type BadgeRenderSize = 'xs' | 'sm' | 'md' | 'lg';

/** Soft cap for card rails — enough for awards + clubs without flooding the payload. */
export const CARD_BADGE_RAIL_MAX = 12;

function sizePx(size: BadgeRenderSize): number {
  if (size === 'xs') return 26;
  if (size === 'sm') return 36;
  if (size === 'lg') return 96;
  return 52;
}

function labelForSize(label: string, size: BadgeRenderSize): string {
  const clean = label.trim();
  if (size === 'xs') return clean.slice(0, 3);
  if (size === 'sm') return clean.slice(0, 5);
  if (size === 'md') return clean.slice(0, 10);
  return clean.slice(0, 18);
}

function prestigeStroke(tier?: string): { width: number; opacity: number } {
  if (tier === 'major') return { width: 2.5, opacity: 0.95 };
  if (tier === 'award') return { width: 2, opacity: 0.8 };
  if (tier === 'participation' || tier === 'affiliation') return { width: 1.25, opacity: 0.55 };
  return { width: 1, opacity: 0.4 };
}

export function badgeAccessibleName(
  design: BadgeDesign,
  extra?: { verification?: string; issuer?: string; date?: string }
) {
  const parts = [
    design.name || design.short_label,
    design.badge_category,
    design.prestige_tier !== design.badge_category ? design.prestige_tier : null,
    extra?.verification,
    extra?.issuer ? `issued by ${extra.issuer}` : null,
    extra?.date,
  ].filter(Boolean);
  return parts.join(', ');
}

interface BadgeRendererProps {
  design: BadgeDesign;
  size?: BadgeRenderSize;
  className?: string;
  title?: string;
  onClick?: () => void;
  surface?: 'dark' | 'light';
}

export const BadgeRenderer: React.FC<BadgeRendererProps> = ({
  design,
  size = 'md',
  className = '',
  title,
  onClick,
  surface = 'dark',
}) => {
  // Card-scale badges stay circular so labels never get crushed by exotic clip paths.
  const shape = size === 'xs' || size === 'sm' ? 'roundel' : design.shape || 'roundel';
  const path = shapePath(shape);
  const px = sizePx(size);
  const label = labelForSize(design.short_label || design.name || '', size);
  const a11y = title || badgeAccessibleName(design);
  const stroke = prestigeStroke(design.prestige_tier);
  const primary = design.primary_color || '#1A1A1A';
  const secondary = design.secondary_color || '#F97316';
  const accent = design.accent_color || '#FAFAFA';
  const gradId = useMemo(() => {
    const raw = `${design.name || ''}-${design.short_label || ''}-${shape}-${size}`;
    return `bg-${raw.replace(/[^a-zA-Z0-9]/g, '').slice(0, 28) || 'badge'}`;
  }, [design.name, design.short_label, shape, size]);

  const fontSize = size === 'xs' ? 9 : size === 'sm' ? 11 : size === 'lg' ? 13 : 10;
  const yearSize = size === 'lg' ? 8 : 6.5;
  const showYear = Boolean(design.year_label) && size !== 'sm' && size !== 'xs';

  const svg = (
    <svg
      width={px}
      height={px}
      viewBox="0 0 64 64"
      className={`block shrink-0 ${className}`}
      role={onClick ? undefined : 'img'}
      aria-hidden={onClick ? true : undefined}
      aria-label={onClick ? undefined : a11y}
      style={{
        filter:
          surface === 'light'
            ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.18))'
            : 'drop-shadow(0 1px 3px rgba(0,0,0,0.45))',
      }}
    >
      <title>{a11y}</title>
      <defs>
        <linearGradient id={gradId} x1="12" y1="8" x2="52" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={secondary} />
          <stop offset="55%" stopColor={primary} />
          <stop offset="100%" stopColor={secondary} stopOpacity="0.92" />
        </linearGradient>
      </defs>
      <path
        d={path}
        fill={`url(#${gradId})`}
        stroke={accent}
        strokeOpacity={stroke.opacity}
        strokeWidth={stroke.width}
        strokeLinejoin="round"
      />
      {/* Inner enamel disc for readable type on ornate shapes */}
      {shape !== 'roundel' && shape !== 'circle' ? (
        <circle cx="32" cy={showYear ? 28 : 30} r={size === 'lg' ? 16 : 13} fill={primary} fillOpacity="0.55" />
      ) : null}
      <text
        x="32"
        y={showYear ? 29 : 33}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={accent}
        fontSize={fontSize}
        fontWeight="700"
        fontFamily="Outfit, Work Sans, system-ui, sans-serif"
        letterSpacing="0.04em"
        style={{ textTransform: 'uppercase' }}
      >
        {label}
      </text>
      {showYear ? (
        <text
          x="32"
          y="44"
          textAnchor="middle"
          dominantBaseline="middle"
          fill={accent}
          fillOpacity="0.85"
          fontSize={yearSize}
          fontFamily="Work Sans, system-ui, sans-serif"
          fontWeight="500"
        >
          {design.year_label}
        </text>
      ) : null}
      <desc>{ICON_LABELS[design.icon_key] || design.icon_key}</desc>
    </svg>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="inline-flex cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 transition-opacity duration-200 hover:opacity-90"
        aria-label={a11y}
        title={a11y}
      >
        {svg}
      </button>
    );
  }

  return svg;
};

function dedupeBadges(badges: IssuedBadge[]): IssuedBadge[] {
  const seen = new Set<string>();
  const out: IssuedBadge[] = [];
  for (const b of badges) {
    const key =
      (b.badge_definition_id != null ? `def:${b.badge_definition_id}` : null) ||
      (b.design?.name ? `name:${b.design.name}` : null) ||
      (b.public_id ? `pid:${b.public_id}` : null) ||
      `id:${b.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(b);
  }
  return out;
}

interface BadgeStackProps {
  badges: IssuedBadge[];
  overflow?: number;
  /** `overlap` = Pokémon gym-badge rail (default for cards). `row` = spaced list. */
  variant?: 'overlap' | 'row';
  size?: BadgeRenderSize;
  maxVisible?: number;
  className?: string;
  onBadgeClick?: (badge: IssuedBadge) => void;
  onOverflowClick?: () => void;
}

export const BadgeStack: React.FC<BadgeStackProps> = ({
  badges,
  overflow = 0,
  variant = 'overlap',
  size = 'xs',
  maxVisible = CARD_BADGE_RAIL_MAX,
  className = '',
  onBadgeClick,
  onOverflowClick,
}) => {
  const unique = useMemo(() => dedupeBadges(badges), [badges]);
  if (!unique.length && overflow <= 0) return null;

  const cap = Math.max(1, maxVisible);
  const shown = unique.slice(0, cap);
  const hidden = Math.max(0, unique.length - shown.length) + overflow;
  const names = shown
    .map((b) => b.design?.name || b.design?.short_label)
    .filter(Boolean)
    .join(', ');
  const groupLabel = hidden > 0
    ? `Recognition badges: ${names}, and ${hidden} more`
    : `Recognition badges: ${names || shown.length}`;

  if (variant === 'row') {
    return (
      <div className={`flex flex-wrap items-center gap-2 ${className}`} aria-label={groupLabel}>
        {shown.map((b) => (
          <BadgeRenderer
            key={b.public_id || b.id}
            design={b.design}
            size={size === 'xs' ? 'sm' : size}
            onClick={onBadgeClick ? () => onBadgeClick(b) : undefined}
          />
        ))}
        {hidden > 0 ? (
          <button
            type="button"
            onClick={onOverflowClick}
            className="text-xs text-gray-400 hover:text-orange-400 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40 rounded-md px-1.5 min-h-[36px] sm:min-h-0 transition-colors duration-200"
            aria-label={`${hidden} more badges`}
          >
            +{hidden}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`badge-stack-overlap ${className}`}
      aria-label={groupLabel}
      title={groupLabel}
    >
      {shown.map((b, i) => (
        <div
          key={b.public_id || b.id}
          className="badge-stack-overlap__item"
          style={{ zIndex: shown.length - i }}
        >
          <BadgeRenderer
            design={b.design}
            size={size}
            onClick={onBadgeClick ? () => onBadgeClick(b) : undefined}
          />
        </div>
      ))}
      {hidden > 0 ? (
        <button
          type="button"
          onClick={onOverflowClick}
          className="badge-stack-overlap__more"
          style={{ zIndex: 0 }}
          aria-label={`${hidden} more badges`}
          title={`${hidden} more badges`}
        >
          +{hidden > 99 ? '99' : hidden}
        </button>
      ) : null}
    </div>
  );
};

export default BadgeRenderer;
